import React, { useState, useEffect, useContext } from "react";
import { Avatar, Box, Button, Card, CardActions, CardContent, IconButton, Stack, Tab, Tabs } from '@mui/material';
import { Link } from "react-router-dom";
import { PrivateRoutesContext } from "app/PrivateRoutesContext";
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import QueryBuilderOutlinedIcon from '@mui/icons-material/QueryBuilderOutlined';

import LoaderAnimation from "pages/LoaderAnimation";

import { useTheme } from '@mui/material/styles';
import { apiGet } from "app/Services/ApiService";
import { employeeApiUrls } from "common/ApiUrls";
import { Utils } from "common/Utils";
import { PortalNames } from "common/Portals";

import AximDoughnut from "components/Content/Graphs/AximDoughnut";

import moment from "moment";
import AximTooltip from "components/AximTooltip";
  
  function TabPanel(props) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
  }

  function a11yProps(index) {
    return {
      id: `vertical-tab-${index}`,
      'aria-controls': `vertical-tabpanel-${index}`,
    };
  }

function Dashboard() {
    const { insuredId } = useContext(PrivateRoutesContext);

    const [hasLoadError, setHasLoadError] = useState(false);
    const [hwPeriods, setHwPeriods] = useState(null);
    const [hwDashboardDetails, setHwDashboardDetails] = useState([]);
    const [value, setValue] = useState(0);
    
    const theme = useTheme();

    const handleChange = (event, newValue) => {
        setValue(newValue);

        var hwBenefitDate = hwPeriods[newValue].hwbenefitDate;

        if (!hwDashboardDetails[newValue].hasData) {
            var details = [...hwDashboardDetails];
            details[newValue].isLoading = true;
            setHwDashboardDetails(details);

            apiGet(employeeApiUrls.getDashboardDataHwBenefit(insuredId, hwBenefitDate)).then((response) => {
                addDashboardDetails(newValue, [...hwDashboardDetails], response.data);
            })
            .catch((response) => {
                var errorDetails = [...hwDashboardDetails];
                errorDetails[newValue].isLoading = true;
                setHwDashboardDetails(errorDetails);
            })
        }
    };

    useEffect(() => {
        if (insuredId) {
            apiGet(employeeApiUrls.getDashboardData(insuredId)).then((response) => {
                if (response.data.hwPeriods) {
                    setHwPeriods(response.data.hwPeriods);

                    var details = response.data.hwPeriods.map(p => {
                        return {
                            isLoading: false,
                            hasData: false,
                        };
                    });

                    addDashboardDetails(0, details, response.data);
                }
            })
            .catch((response) => {
                setHasLoadError(true);
            })
        }
    },[insuredId])

    const addDashboardDetails = (index, details, data) => {
        if (data.contracts) {
            details[index].hwBenefitDate = data.hwBenefitDate;
            details[index].contracts = data.contracts;
            details[index].contributions = data.contributions;
            details[index].hourbanks = data.hourbanks;
            details[index].hasData = true;
            details[index].isLoading = false;
        }

        setHwDashboardDetails(details);
    }

    const cardLine = (label, value, index, indent) => {
        return (
            <Box key={index ?? 0} className="d-flex" style={{ justifyContent: "space-between"}}>
                <Box className={`ps-${indent ?? 0} pe-2`} color="primary.main">{label}</Box>
                <Box>{value}</Box>
            </Box>
        );
    }

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.paper', display: 'flex' }}>
    {hwPeriods ?
    <Tabs
        orientation="vertical"
        variant="scrollable"
        value={value}
        onChange={handleChange}
        sx={{ borderRight: 1, borderColor: 'divider', height: "calc(100vh - 100px)" }}
      >
        {hwPeriods.map((d,i) =>
            <Tab key={i} label={
                <React.Fragment>
                    <Box>
                        {`H&W Benefit Date: ${Utils.formatDate(d.hwbenefitDate)}`}
                    </Box>
                    <Box>
                        {`H&W Due: ${Utils.formatCurrency(d.sumActualHwdue)}`}
                    </Box>
                    <Box>
                        {`Total Hours: ${d.sumActualHours}`}
                    </Box>
                </React.Fragment>
            } {...a11yProps(i)}/>
        )}
      </Tabs> :
      hasLoadError ? <Box>Unable to load Dashboard</Box> :
      <LoaderAnimation centerAtPage={true} />}
      {hwDashboardDetails.map((d,i) =>
        <TabPanel value={value} index={i} style={{width: "-webkit-fill-available"}}>
            {d.isLoading ? <LoaderAnimation /> :
            !d.hasData ? <Box>No data</Box> :
            <Box>
                <Box component={"h3"}>
                    {`H&W Benefit Date: ${Utils.formatDate(d.hwBenefitDate)}`}
                    <AximTooltip title={"View H&W Statement"}>
                        <Button>
                            <Link
                                to={`/${PortalNames.Employee.Path}/${PortalNames.Employee.Pages.HWStatements}/${moment(d.hwBenefitDate).format("YYYY-MM-DD")}`}
                                style={{ textDecoration: "none", color: theme.palette.primary.main }}
                            >
                                <ArticleOutlinedIcon />
                            </Link>
                        </Button>
                    </AximTooltip>
                </Box>
                <Box className="d-flex m-0 g-0">
                    <Box>
                        <Stack spacing={1}>
                            <Box fontWeight={600}>Contracts</Box>
                            {d.contracts.map((e,i) => 
                                <Card key={i} className="p-1" sx={{ minWidth: 200}}>
                                    {/* <CardContent> */}
                                        {cardLine("Contract Code:", e.contractCode, 0)}
                                        {cardLine(
                                            <>
                                                {"Pay Date:"}
                                                <AximTooltip title={"View Timesheet"}>
                                                    <IconButton size="small">
                                                        <Link
                                                            to={`/${PortalNames.Employee.Path}/${PortalNames.Employee.Pages.TimeSheet}/${moment(e.payDate).format("YYYY-MM-DD")}`}
                                                            style={{ textDecoration: "none", color: theme.palette.primary.main }}
                                                        >
                                                            <QueryBuilderOutlinedIcon />
                                                        </Link>
                                                    </IconButton>
                                                </AximTooltip>
                                            </>,
                                            Utils.formatDate(e.payDate, 1))
                                        }
                                        {e.hours.map((f,j) => cardLine(`${f.type}:`, f.hours, 10+j, 2))}
                                        {cardLine("Total Hours:", e.totalHours, 2)}
                                        {cardLine("H&W Due:", Utils.formatCurrency(e.hwDue), 3)}
                                    {/* </CardContent> */}
                                </Card>
                            )}
                        </Stack>
                    </Box>
                    <Box className="flex-grow-1" sx={{textAlign: "-webkit-center"}}>
                        <AximDoughnut data={d.contributions} isCurrency={true} />
                    </Box>
                    <Box className="">
                        <Stack spacing={1}>
                            {d.hourbanks.map((h,i) => 
                                <Card key={i} className="p-1" sx={{ minWidth: 200}}>
                                    <Box fontWeight={600}>{h.type}</Box>
                                    {cardLine("Beginning:", Utils.formatCurrency(h.beginningBalance), 0)}
                                    {cardLine("Contribution:", Utils.formatCurrency(h.contribution), 1)}
                                    {cardLine("Deduction:", Utils.formatCurrency(h.deduction), 2)}
                                    {cardLine("Ending:", Utils.formatCurrency(h.endingBalance), 3)}
                                </Card>
                            )}
                        </Stack>
                    </Box>
                </Box>
            </Box>}
        </TabPanel>
      )}
    </Box>
  );
}

export default Dashboard;
